/* eslint-disable @wyz/module/no-relative-import */
import { IOptionItem, TFunction } from '@wyz/types';
import { DateUtils, invariant } from '@wyz/utils';
import { isNil, set } from 'lodash-es';
import {
  ICustomer,
  IRimOptionItem,
  ITyreCountOptionItem,
  IVehicleTypeGroupList,
  IVehicleTypeGroupsOptionItem,
  TranslationKeys,
} from '~/common/types';
import {
  ICreateQuoteServicePricingPolicyPayload,
  ICustomQuoteService,
  IGlobalQuoteServicePricingFormData,
  IPolicyDefinitionValue,
  IPricingStatus,
  IQuoteServiceGeometryStepForm,
  IQuoteServiceMountingStepForm,
  IQuoteServicePricingDefinitionPayload,
  IQuoteServiceType,
  ITyreCountType,
  IQuoteServicePricingPolicyFormStep,
  IGetOneQuoteServicePricingPolicy,
  IGlobalQuoteDefaultServicePricingFormData,
} from '~/purchase/types';
import { DEFAULT_VEHICLE_TYPE } from '~/purchase/helpers';
import {
  PRICE_TYPE_ENUM,
  PRICING_POLICY_STATUS,
  SERVICE_TYPE,
  TYRE_COUNT_ENUM,
} from '~/purchase/schemas';

import {
  CUSTOMER_CRITERIA_CODE,
  NO_REFINEMENT_VALUE,
  buildVehicleTypeLookup,
  getDiameterPayload,
  getLookupValue,
} from './common';

export const RUN_FLAT_VALUE = 'RUNFLAT';
// Dev's note : The order is very important, we use this array as data source for the UI Stepper
export const SERVICE_PRICING_POLICY_STEP_LABELS: Record<
  IQuoteServicePricingPolicyFormStep,
  keyof TranslationKeys
> = {
  MOUNTING: 'app.common.quote.mounting',
  GEOMETRY_SERVICE: 'app.quote.pricing_policy.service.geometry.step',
  APPLICATION: 'app.quote.pricing_policy.form.step3.wizard_title',
};
export const SERVICE_PRICING_POLICY_STEP_INDEX: Record<
  IQuoteServicePricingPolicyFormStep,
  number
> = {
  APPLICATION: 0,
  MOUNTING: 1,
  GEOMETRY_SERVICE: 2,
} as const;
export const FIRST_STEP = Math.min(
  ...Object.values(SERVICE_PRICING_POLICY_STEP_INDEX),
);
export const LAST_STEP = Math.max(
  ...Object.values(SERVICE_PRICING_POLICY_STEP_INDEX),
);

export const hasRefined = (
  refinementFlags: IQuoteServiceMountingStepForm['refinementFlags'],
) => {
  return (
    refinementFlags.diameter ||
    refinementFlags.rim ||
    refinementFlags.tyreCount ||
    refinementFlags.tyreType
  );
};

export const getRims = (
  options: Array<IRimOptionItem>,
  flags: IQuoteServiceMountingStepForm['refinementFlags'],
): Array<IRimOptionItem> => {
  if (flags.rim) {
    return options;
  }
  return [{ label: NO_REFINEMENT_VALUE, value: NO_REFINEMENT_VALUE }];
};

export const getTyreCountColumns = (
  options: Array<ITyreCountOptionItem>,
  isRefinedByTyreCount: boolean,
  exclude: Array<ITyreCountType> = [],
): Array<ITyreCountOptionItem> => {
  if (isRefinedByTyreCount) {
    return options.filter((option) => {
      return !exclude.includes(option.value);
    });
  }
  return [{ label: NO_REFINEMENT_VALUE, value: NO_REFINEMENT_VALUE }];
};
export const getTyreTypeColumns = (
  t: TFunction<TranslationKeys>,
  allVehicleTypeGroups: IVehicleTypeGroupList['data'],
  selectedVehicleTypeGroup: string,
  refineByTyreType: boolean,
  includeRunFlat = false,
): Array<IOptionItem> => {
  if (refineByTyreType) {
    const base =
      allVehicleTypeGroups
        .find((item) => item.publicKey === selectedVehicleTypeGroup)
        ?.vehicleTypes.map((vehicleType) => ({
          label: t(vehicleType.i18nKey),
          value: vehicleType.publicKey,
        })) ?? [];
    if (includeRunFlat) {
      base.push({ label: RUN_FLAT_VALUE, value: RUN_FLAT_VALUE });
    }
    return base;
  }
  return [{ label: '', value: NO_REFINEMENT_VALUE }];
};

export const formToPayload = (
  formData: IGlobalQuoteServicePricingFormData,
  status: IPricingStatus,
  customerId: string,
  vehicleTypeGroups: IVehicleTypeGroupList['data'],
): ICreateQuoteServicePricingPolicyPayload => {
  invariant(formData.MOUNTING);
  invariant(formData.GEOMETRY_SERVICE);
  invariant(formData.APPLICATION);
  const { vehicleTypeGroupLookup, vehicleTypeLookup } =
    buildVehicleTypeLookup(vehicleTypeGroups);

  return {
    pricingPolicyType: 'quote_service',
    name: formData.MOUNTING.name,
    vehicleTypeGroup: getLookupValue(
      formData.MOUNTING.vehicleTypeGroup,
      vehicleTypeGroupLookup,
    ),
    startDate: DateUtils.formatTimestamp(formData.APPLICATION?.startDate, {
      customFormat: DateUtils.CUSTOM_DATE_FORMAT.DMY,
    }),
    endDate: formData.APPLICATION?.endDate
      ? DateUtils.formatTimestamp(formData.APPLICATION?.endDate, {
          customFormat: DateUtils.CUSTOM_DATE_FORMAT.DMY,
        })
      : undefined,
    status: status,
    criteria: [
      {
        criterion: '',
        id: '',
        code: 'CUSTOMER_ID',
        value: customerId,
      },
    ],
    definitions: [
      ...flattenMountingServices(formData.MOUNTING, vehicleTypeLookup),
      ...flattenGeometryServices(formData.GEOMETRY_SERVICE, vehicleTypeLookup),
      ...flattenCustomServices(formData.GEOMETRY_SERVICE),
    ],
    default: false,
    priceType: formData.MOUNTING.priceType,
  };
};

export function payloadToForm(
  payload: IGetOneQuoteServicePricingPolicy,
): IGlobalQuoteServicePricingFormData {
  const prices: Record<
    IQuoteServiceType,
    IGetOneQuoteServicePricingPolicy['definitions']
  > = payload.definitions.reduce(
    (prices, definition) => ({
      ...prices,
      [definition.serviceType]: [...prices[definition.serviceType], definition],
    }),
    {
      [SERVICE_TYPE.MOUNTING]: [],
      [SERVICE_TYPE.GEOMETRY]: [],
      [SERVICE_TYPE.CUSTOM]: [],
    },
  );

  return {
    MOUNTING: {
      name: payload.name,
      globalValue:
        prices.MOUNTING.length === 1
          ? prices.MOUNTING[0].priceValue.c
          : undefined,
      rims: getMountingRims(prices.MOUNTING),
      vehicleTypeGroup: payload.vehicleTypeGroup.key,
      refinementFlags: {
        diameter: Boolean(prices.MOUNTING[0].diameter),
        rim: Boolean(prices.MOUNTING[0].rim),
        tyreType: Boolean(prices.MOUNTING[0].tyreType),
        tyreCount: Boolean(prices.MOUNTING[0].tyreCount),
      },
      priceType: payload.priceType,
    },
    GEOMETRY_SERVICE: {
      refinementFlags: {
        tyreType: Boolean(prices.GEOMETRY[0].tyreType),
      },
      geometry: {
        tyreCounts: prices.GEOMETRY.reduce<
          IQuoteServiceGeometryStepForm['geometry']['tyreCounts']
        >((geometry, definition) => {
          const result = { ...geometry };
          const tyreCount = isNil(definition.tyreCount)
            ? NO_REFINEMENT_VALUE
            : Object.entries(TYRE_COUNT_ENUM).find(
                ([, count]) => count === definition.tyreCount,
              )?.[0];
          invariant(tyreCount);
          const vehicleType = isNil(definition.tyreType)
            ? NO_REFINEMENT_VALUE
            : definition.tyreType.key;
          if (isNil(result[tyreCount])) {
            result[tyreCount] = {
              tyreTypes: {},
              code: definition.serviceCode ?? '',
              description: definition.description ?? '',
            };
          }
          result[tyreCount].tyreTypes[vehicleType] = {
            value: definition.priceValue.c,
          };
          return result;
        }, {}),
      },
      hasCustomerServices: prices.CUSTOM.length > 0,
      customServices:
        prices.CUSTOM.length === 0
          ? [getCustomServiceDefaultValue()]
          : prices.CUSTOM.map((definition) => ({
              price: definition.priceValue.c,
              code: definition.serviceCode ?? '',
              description: definition.description ?? '',
              active: definition.serviceStatus === 'ENABLED',
            })),
    },
    APPLICATION: {
      startDate: new Date(payload.formattedStartDate),
      endDate: payload.formattedEndDate
        ? new Date(payload.formattedEndDate)
        : undefined,
    },
  };
}

export function payloadToFormForDefault(
  payload: IGetOneQuoteServicePricingPolicy,
  fetchedCustomers: ICustomer[],
): IGlobalQuoteDefaultServicePricingFormData {
  const prices: Record<
    IQuoteServiceType,
    IGetOneQuoteServicePricingPolicy['definitions']
  > = payload.definitions.reduce(
    (prices, definition) => ({
      ...prices,
      [definition.serviceType]: [...prices[definition.serviceType], definition],
    }),
    {
      [SERVICE_TYPE.MOUNTING]: [],
      [SERVICE_TYPE.GEOMETRY]: [],
      [SERVICE_TYPE.CUSTOM]: [],
    },
  );

  return {
    MOUNTING: {
      name: payload.name,
      globalValue:
        prices.MOUNTING.length === 1
          ? prices.MOUNTING[0].priceValue.c
          : undefined,
      rims: getMountingRims(prices.MOUNTING),
      vehicleTypeGroup: payload.vehicleTypeGroup.key,
      refinementFlags: {
        diameter: Boolean(prices.MOUNTING[0].diameter),
        rim: Boolean(prices.MOUNTING[0].rim),
        tyreType: Boolean(prices.MOUNTING[0].tyreType),
        tyreCount: Boolean(prices.MOUNTING[0].tyreCount),
      },
      priceType: payload.priceType,
    },
    GEOMETRY_SERVICE: {
      refinementFlags: {
        tyreType: Boolean(prices.GEOMETRY[0].tyreType),
      },
      geometry: {
        tyreCounts: prices.GEOMETRY.reduce<
          IQuoteServiceGeometryStepForm['geometry']['tyreCounts']
        >((geometry, definition) => {
          const result = { ...geometry };
          const tyreCount = isNil(definition.tyreCount)
            ? NO_REFINEMENT_VALUE
            : Object.entries(TYRE_COUNT_ENUM).find(
                ([, count]) => count === definition.tyreCount,
              )?.[0];
          invariant(tyreCount);
          const vehicleType = isNil(definition.tyreType)
            ? NO_REFINEMENT_VALUE
            : definition.tyreType.key;
          if (isNil(result[tyreCount])) {
            result[tyreCount] = {
              tyreTypes: {},
              code: definition.serviceCode ?? '',
              description: definition.description ?? '',
            };
          }
          result[tyreCount].tyreTypes[vehicleType] = {
            value: definition.priceValue.c,
          };
          return result;
        }, {}),
      },
      hasCustomerServices: prices.CUSTOM.length > 0,
      customServices:
        prices.CUSTOM.length === 0
          ? [getCustomServiceDefaultValue()]
          : prices.CUSTOM.map((definition) => ({
              price: definition.priceValue.c,
              code: definition.serviceCode ?? '',
              description: definition.description ?? '',
              active: definition.serviceStatus === 'ENABLED',
            })),
    },
    APPLICATION: {
      startDate: new Date(payload.formattedStartDate),
      endDate: payload.formattedEndDate
        ? new Date(payload.formattedEndDate)
        : undefined,
      customers: payload.criteria.map((criterion) => criterion.value),
      customersAutocomplete: fetchedCustomers.map((customer) => ({
        label: `${customer.name} (${customer.code})`,
        value: customer.id,
      })),
    },
  };
}

const flattenMountingServices = (
  mountingFormData: IQuoteServiceMountingStepForm,
  vehicleTypeLookup: Record<string, IPolicyDefinitionValue>,
): IQuoteServicePricingDefinitionPayload[] => {
  const definitions: IQuoteServicePricingDefinitionPayload[] = [];
  if (!hasRefined(mountingFormData.refinementFlags)) {
    definitions.push({
      priceValue: {
        c: mountingFormData.globalValue ?? 0,
        cg1: 0,
        cg2: 0,
        cg3: 0,
        cg4: 0,
        cg5: 0,
      },
      diameter: null,
      tyreType: null,
      tyreCount: null,
      serviceCode: '',
      description: '',
      serviceStatus: PRICING_POLICY_STATUS.ENABLED,
      serviceType: SERVICE_TYPE.MOUNTING,
    });
  } else {
    Object.entries(mountingFormData.rims || {}).forEach(
      ([rimCodeName, rims]) => {
        const refinements = rims.refinements || [];
        refinements.forEach((refinement) => {
          Object.entries(refinement?.tyreCount || {}).forEach(
            ([tyreCountCodeName, tyreTypes]) => {
              Object.entries(tyreTypes.tyreTypes || {}).forEach(
                ([vehicleTypePublicKey, values]) => {
                  const diameter = getDiameterPayload(refinement?.diameter);
                  const tyreType =
                    vehicleTypePublicKey === NO_REFINEMENT_VALUE
                      ? null
                      : vehicleTypePublicKey === RUN_FLAT_VALUE
                        ? { key: RUN_FLAT_VALUE, name: RUN_FLAT_VALUE }
                        : getLookupValue(
                            vehicleTypePublicKey,
                            vehicleTypeLookup,
                            false,
                          );
                  // null is a valid value, undefined is not
                  if (tyreType !== undefined) {
                    definitions.push({
                      diameter: diameter,
                      priceValue: {
                        c: values.value,
                        cg1: 0,
                        cg2: 0,
                        cg3: 0,
                        cg4: 0,
                        cg5: 0,
                      },
                      rim:
                        rimCodeName === NO_REFINEMENT_VALUE
                          ? undefined
                          : rimCodeName,
                      tyreType: tyreType,
                      tyreCount:
                        TYRE_COUNT_ENUM[
                          tyreCountCodeName as Exclude<ITyreCountType, 'ALL'>
                        ],
                      serviceType: 'MOUNTING',
                      serviceCode: '',
                      description: '',
                      serviceStatus: PRICING_POLICY_STATUS.ENABLED,
                    });
                  }
                },
              );
            },
          );
        });
      },
    );
  }

  return definitions;
};

const flattenGeometryServices = (
  formData: IQuoteServiceGeometryStepForm,
  vehicleTypeLookup: Record<string, IPolicyDefinitionValue>,
): IQuoteServicePricingDefinitionPayload[] => {
  const definitions: IQuoteServicePricingDefinitionPayload[] = [];
  Object.entries(formData.geometry.tyreCounts).forEach(
    ([tyreCountCodeName, tyreCountValue]) => {
      Object.entries(tyreCountValue.tyreTypes).forEach(
        ([tyreTypeKey, values]) => {
          const tyreType =
            tyreTypeKey === NO_REFINEMENT_VALUE
              ? null
              : getLookupValue(tyreTypeKey, vehicleTypeLookup, false);
          // null is a valid value, undefined is not
          if (tyreType !== undefined) {
            definitions.push({
              priceValue: {
                c: values.value,
                cg1: 0,
                cg2: 0,
                cg3: 0,
                cg4: 0,
                cg5: 0,
              },
              diameter: null,
              tyreType: tyreType,
              tyreCount:
                TYRE_COUNT_ENUM[
                  tyreCountCodeName as Exclude<ITyreCountType, 'ALL'>
                ],
              serviceCode: tyreCountValue.code,
              description: tyreCountValue.description,
              serviceStatus: PRICING_POLICY_STATUS.ENABLED,
              serviceType: SERVICE_TYPE.GEOMETRY,
            });
          }
        },
      );
    },
  );
  return definitions;
};

const flattenCustomServices = (
  formData: IQuoteServiceGeometryStepForm,
): IQuoteServicePricingDefinitionPayload[] => {
  const definitions: IQuoteServicePricingDefinitionPayload[] = [];
  // skip empty lines
  formData.customServices
    .filter(
      (customService) =>
        customService.price &&
        customService.code.trim() &&
        customService.description.trim(),
    )
    .forEach((customService) => {
      definitions.push({
        priceValue: {
          c: Number(customService.price),
          cg1: 0,
          cg2: 0,
          cg3: 0,
          cg4: 0,
          cg5: 0,
        },
        diameter: null,
        tyreType: null,
        tyreCount: null,
        serviceCode: customService.code,
        description: customService.description,
        serviceStatus: customService.active
          ? PRICING_POLICY_STATUS.ENABLED
          : PRICING_POLICY_STATUS.DISABLED,
        serviceType: SERVICE_TYPE.CUSTOM,
      });
    });
  return definitions;
};

type InitialValuesOptions = {
  vehicleTypeGroups: Array<IVehicleTypeGroupsOptionItem>;
  t: TFunction<TranslationKeys>;
};

export const getCustomServiceDefaultValue = (): ICustomQuoteService => ({
  code: '',
  description: '',
  price: undefined,
  active: true,
});

export const getInitialValues = ({
  vehicleTypeGroups,
  t,
}: InitialValuesOptions): IGlobalQuoteServicePricingFormData => {
  const SERVICE_PRICING_POLICY_DEFAULT_VALUES: IGlobalQuoteServicePricingFormData =
    {
      MOUNTING: {
        rims: {
          [NO_REFINEMENT_VALUE]: {
            refinements: [{ tyreCount: {} }],
          },
        },

        globalValue: undefined,
        refinementFlags: {
          diameter: false,
          rim: false,
          tyreCount: false,
          tyreType: false,
        },
        name: '',
        vehicleTypeGroup:
          vehicleTypeGroups.find((item) => item.value === DEFAULT_VEHICLE_TYPE)
            ?.value ??
          vehicleTypeGroups.at(0)?.value ??
          '',
        priceType: PRICE_TYPE_ENUM.TAX_INCLUDED,
      },
      GEOMETRY_SERVICE: {
        refinementFlags: { tyreType: true },
        geometry: {
          tyreCounts: {
            ['TWO_TYRE']: {
              tyreTypes: {},
              code: 'GEO2',
              description: t(
                'app.quote.pricing_policy.service.geometry.two_wheels.description_placeholder',
              ),
            },

            ['FOUR_TYRE']: {
              tyreTypes: {},
              code: 'GEO4',
              description: t(
                'app.quote.pricing_policy.service.geometry.four_wheels.description_placeholder',
              ),
            },
          },
        },
        hasCustomerServices: false,
        customServices: [getCustomServiceDefaultValue()],
      },
      APPLICATION: undefined,
    };
  return {
    ...SERVICE_PRICING_POLICY_DEFAULT_VALUES,
  };
};

export const CUSTOM_SERVICE_SUGGESTIONS: Array<keyof TranslationKeys> = [
  'app.quote.pricing_policy.suggestions.axle_reversal',
  'app.quote.pricing_policy.suggestions.guarding_2_tyre',
  'app.quote.pricing_policy.suggestions.guarding_4_tyre',
  'app.quote.pricing_policy.suggestions.nitrogen_inflation',
];

function getMountingRims(
  mountingPrices: ICreateQuoteServicePricingPolicyPayload['definitions'],
): IQuoteServiceMountingStepForm['rims'] {
  return mountingPrices.reduce<IQuoteServiceMountingStepForm['rims']>(
    (rims, mountingPrice) => {
      const rim = isNil(mountingPrice.rim)
        ? NO_REFINEMENT_VALUE
        : mountingPrice.rim;
      const tyreCount = isNil(mountingPrice.tyreCount)
        ? NO_REFINEMENT_VALUE
        : Object.entries(TYRE_COUNT_ENUM).find(
            ([, count]) => count === mountingPrice.tyreCount,
          )?.[0];
      invariant(tyreCount);
      const vehicleType = isNil(mountingPrice.tyreType)
        ? NO_REFINEMENT_VALUE
        : mountingPrice.tyreType.key;
      const result = { ...rims };
      if (isNil(result[rim])) {
        result[rim] = {
          refinements: [],
        };
      }
      const diameterIndex = result[rim].refinements?.findIndex(
        (refinement) =>
          refinement?.diameter?.to === mountingPrice.diameter?.to &&
          refinement?.diameter?.from === mountingPrice.diameter?.from,
      );
      if (isNil(diameterIndex) || diameterIndex === -1) {
        result[rim].refinements!.push({
          diameter: isNil(mountingPrice.diameter)
            ? null
            : {
                from: mountingPrice.diameter.from,
                to: mountingPrice.diameter.to,
                operator: mountingPrice.diameter.operator,
              },
          tyreCount: {
            [tyreCount]: {
              tyreTypes: {
                [vehicleType]: { value: mountingPrice.priceValue.c },
              },
            },
          },
        });
      } else {
        set(
          result,
          [
            rim,
            'refinements',
            diameterIndex,
            'tyreCount',
            tyreCount,
            'tyreTypes',
            vehicleType,
            'value',
          ],
          mountingPrice.priceValue.c,
        );
      }
      return result;
    },
    {},
  );
}

export const formToPayloadForDefault = (
  formData: IGlobalQuoteDefaultServicePricingFormData,
  status: IPricingStatus,
  vehicleTypeGroups: IVehicleTypeGroupList['data'],
): ICreateQuoteServicePricingPolicyPayload => {
  invariant(formData.MOUNTING);
  invariant(formData.GEOMETRY_SERVICE);
  invariant(formData.APPLICATION);
  const { vehicleTypeGroupLookup, vehicleTypeLookup } =
    buildVehicleTypeLookup(vehicleTypeGroups);

  return {
    pricingPolicyType: 'quote_service',
    name: formData.MOUNTING.name,
    vehicleTypeGroup: getLookupValue(
      formData.MOUNTING.vehicleTypeGroup,
      vehicleTypeGroupLookup,
    ),
    startDate: DateUtils.formatTimestamp(formData.APPLICATION?.startDate, {
      customFormat: DateUtils.CUSTOM_DATE_FORMAT.DMY,
    }),
    endDate: formData.APPLICATION?.endDate
      ? DateUtils.formatTimestamp(formData.APPLICATION?.endDate, {
          customFormat: DateUtils.CUSTOM_DATE_FORMAT.DMY,
        })
      : undefined,
    status: status,
    default: true,
    criteria: [
      ...formData.APPLICATION.customers.map((customerId: string) => ({
        criterion: '',
        id: '',
        code: CUSTOMER_CRITERIA_CODE,
        value: customerId,
      })),
    ],
    definitions: [
      ...flattenMountingServices(formData.MOUNTING, vehicleTypeLookup),
      ...flattenGeometryServices(formData.GEOMETRY_SERVICE, vehicleTypeLookup),
      ...flattenCustomServices(formData.GEOMETRY_SERVICE),
    ],
    priceType: formData.MOUNTING.priceType,
  };
};
